/**
 * Central localStorage keys for welcome tour, page coaches, feature hints, and one-time toasts.
 * resetAllOnboarding() clears this prefix and legacy coach keys.
 */

import { brand } from '../config/brand';
import { LS_SUPPORT_MODE, LS_ORG_ID } from '../config/storageKeys';

const P = brand.storagePrefix;

export const ONBOARDING_STORAGE_PREFIX = `${P}.onboarding`;

/** Completed when user finishes or skips the welcome tour */
export const WELCOME_TOUR_COMPLETED_KEY = `${ONBOARDING_STORAGE_PREFIX}.welcomeTour.v1`;

export function isWelcomeTourCompleted(): boolean {
  try {
    return localStorage.getItem(WELCOME_TOUR_COMPLETED_KEY) === '1';
  } catch {
    return false;
  }
}

export function markWelcomeTourCompleted(): void {
  try {
    localStorage.setItem(WELCOME_TOUR_COMPLETED_KEY, '1');
  } catch {
    // ignore
  }
}

/** Feature hint popover dismissed permanently */
export function hintStorageKey(hintId: string): string {
  return `${ONBOARDING_STORAGE_PREFIX}.hint.${hintId}`;
}

export function isHintDismissed(hintId: string): boolean {
  try {
    return localStorage.getItem(hintStorageKey(hintId)) === '1';
  } catch {
    return false;
  }
}

export function dismissHint(hintId: string): void {
  try {
    localStorage.setItem(hintStorageKey(hintId), '1');
  } catch {
    // ignore
  }
}

/** One-time toast after first "Add row" on Releases */
export const ADD_ROW_TOAST_KEY = `${ONBOARDING_STORAGE_PREFIX}.toast.addRow.v1`;

export function isAddRowToastCompleted(): boolean {
  try {
    return localStorage.getItem(ADD_ROW_TOAST_KEY) === '1';
  } catch {
    return false;
  }
}

export function markAddRowToastCompleted(): void {
  try {
    localStorage.setItem(ADD_ROW_TOAST_KEY, '1');
  } catch {
    // ignore
  }
}

/** Shown after welcome tour: user dismissed Full Update spotlight or completed first sync */
export const FULL_UPDATE_SPOTLIGHT_KEY = `${ONBOARDING_STORAGE_PREFIX}.fullUpdateSpotlight.v1`;

export function isFullUpdateSpotlightCompleted(): boolean {
  try {
    return localStorage.getItem(FULL_UPDATE_SPOTLIGHT_KEY) === '1';
  } catch {
    return false;
  }
}

export function markFullUpdateSpotlightCompleted(): void {
  try {
    localStorage.setItem(FULL_UPDATE_SPOTLIGHT_KEY, '1');
  } catch {
    // ignore
  }
}

/** Sequential coach after first successful Full Update (per org). */
export type PostUpdateJourneyStep =
  | 'dateFilter'
  | 'tabDetails'
  | 'tabEstimation'
  | 'navigateCalendar'
  | 'navigateReleases'
  | 'completed'
  | 'skipped';

export function postUpdateJourneyStorageKey(orgId: string): string {
  return `${ONBOARDING_STORAGE_PREFIX}.postUpdateJourney.${orgId}.v1`;
}

const POST_UPDATE_JOURNEY_STEPS: PostUpdateJourneyStep[] = [
  'dateFilter',
  'tabDetails',
  'tabEstimation',
  'navigateCalendar',
  'navigateReleases',
];

function isValidPostUpdateJourneyStep(value: string): value is PostUpdateJourneyStep {
  return (
    (POST_UPDATE_JOURNEY_STEPS as string[]).includes(value) ||
    value === 'completed' ||
    value === 'skipped'
  );
}

export function getPostUpdateJourneyStep(orgId: string): PostUpdateJourneyStep | undefined {
  try {
    const raw = localStorage.getItem(postUpdateJourneyStorageKey(orgId));
    if (!raw) return undefined;
    return isValidPostUpdateJourneyStep(raw) ? raw : undefined;
  } catch {
    return undefined;
  }
}

export function setPostUpdateJourneyStep(orgId: string, step: PostUpdateJourneyStep): void {
  try {
    localStorage.setItem(postUpdateJourneyStorageKey(orgId), step);
  } catch {
    // ignore
  }
}

export function advancePostUpdateJourneyStep(orgId: string): void {
  const current = getPostUpdateJourneyStep(orgId);
  if (!current || current === 'completed' || current === 'skipped') return;
  const idx = POST_UPDATE_JOURNEY_STEPS.indexOf(current);
  if (idx === -1) return;
  if (idx >= POST_UPDATE_JOURNEY_STEPS.length - 1) {
    setPostUpdateJourneyStep(orgId, 'completed');
  } else {
    setPostUpdateJourneyStep(orgId, POST_UPDATE_JOURNEY_STEPS[idx + 1]);
  }
}

/** Product-scoped post-update journey (`...postUpdateJourney.<orgId>.<productId>.v2`). */
export function postUpdateJourneyStorageKeyV2(orgId: string, productId: string): string {
  return `${ONBOARDING_STORAGE_PREFIX}.postUpdateJourney.${orgId}.${productId}.v2`;
}

/**
 * Read post-update step for a product. Optionally migrates from v1 once when this product is the
 * org’s default product (preserves existing single-product users).
 */
export function getPostUpdateJourneyStepForProduct(
  orgId: string,
  productId: string,
  defaultProductIdForLegacyMigration?: string | null
): PostUpdateJourneyStep | undefined {
  try {
    const rawV2 = localStorage.getItem(postUpdateJourneyStorageKeyV2(orgId, productId));
    if (rawV2 && isValidPostUpdateJourneyStep(rawV2)) {
      return rawV2 as PostUpdateJourneyStep;
    }
    if (defaultProductIdForLegacyMigration && productId === defaultProductIdForLegacyMigration) {
      const v1 = getPostUpdateJourneyStep(orgId);
      if (v1 !== undefined) {
        localStorage.setItem(postUpdateJourneyStorageKeyV2(orgId, productId), v1);
        return v1;
      }
    }
    return undefined;
  } catch {
    return undefined;
  }
}

export function setPostUpdateJourneyStepForProduct(
  orgId: string,
  productId: string,
  step: PostUpdateJourneyStep
): void {
  try {
    localStorage.setItem(postUpdateJourneyStorageKeyV2(orgId, productId), step);
  } catch {
    // ignore
  }
}

export function advancePostUpdateJourneyStepForProduct(
  orgId: string,
  productId: string,
  defaultProductIdForLegacyMigration?: string | null
): void {
  const current = getPostUpdateJourneyStepForProduct(
    orgId,
    productId,
    defaultProductIdForLegacyMigration
  );
  if (!current || current === 'completed' || current === 'skipped') return;
  const idx = POST_UPDATE_JOURNEY_STEPS.indexOf(current);
  if (idx === -1) return;
  if (idx >= POST_UPDATE_JOURNEY_STEPS.length - 1) {
    setPostUpdateJourneyStepForProduct(orgId, productId, 'completed');
  } else {
    setPostUpdateJourneyStepForProduct(orgId, productId, POST_UPDATE_JOURNEY_STEPS[idx + 1]);
  }
}

/** Forecast page guided tour (per org + product). */
export type ForecastTourStep =
  | 'welcome'
  | 'summary'
  | 'distribute'
  | 'filters'
  | 'tasks'
  | 'completed'
  | 'skipped';

const FORECAST_TOUR_ORDER: ForecastTourStep[] = [
  'welcome',
  'summary',
  'filters',
  'tasks',
  'distribute',
];

export function forecastTourStorageKey(orgId: string, productId: string): string {
  return `${ONBOARDING_STORAGE_PREFIX}.forecastTour.${orgId}.${productId}.v1`;
}

function isValidForecastTourStep(value: string): value is ForecastTourStep {
  return (
    value === 'welcome' ||
    value === 'summary' ||
    value === 'distribute' ||
    value === 'filters' ||
    value === 'tasks' ||
    value === 'completed' ||
    value === 'skipped'
  );
}

export function getForecastTourStep(
  orgId: string,
  productId: string
): ForecastTourStep | undefined {
  try {
    const raw = localStorage.getItem(forecastTourStorageKey(orgId, productId));
    if (!raw) return undefined;
    return isValidForecastTourStep(raw) ? raw : undefined;
  } catch {
    return undefined;
  }
}

export function setForecastTourStep(
  orgId: string,
  productId: string,
  step: ForecastTourStep
): void {
  try {
    localStorage.setItem(forecastTourStorageKey(orgId, productId), step);
  } catch {
    // ignore
  }
}

export function advanceForecastTourStep(orgId: string, productId: string): void {
  const current = getForecastTourStep(orgId, productId);
  if (!current || current === 'completed' || current === 'skipped') return;
  const idx = FORECAST_TOUR_ORDER.indexOf(current);
  if (idx === -1) return;
  if (idx >= FORECAST_TOUR_ORDER.length - 1) {
    setForecastTourStep(orgId, productId, 'completed');
  } else {
    setForecastTourStep(orgId, productId, FORECAST_TOUR_ORDER[idx + 1]);
  }
}

export function retreatForecastTourStep(orgId: string, productId: string): void {
  const current = getForecastTourStep(orgId, productId);
  if (!current || current === 'completed' || current === 'skipped' || current === 'welcome') return;
  const idx = FORECAST_TOUR_ORDER.indexOf(current);
  if (idx <= 0) return;
  setForecastTourStep(orgId, productId, FORECAST_TOUR_ORDER[idx - 1]);
}

/** Releases page guided tour (per org + product). */
export type ReleasesTourStep =
  | 'welcome'
  | 'calendar'
  | 'mapping'
  | 'plans'
  | 'nextForecast'
  | 'completed'
  | 'skipped';

const RELEASES_TOUR_ORDER: ReleasesTourStep[] = [
  'welcome',
  'calendar',
  'mapping',
  'plans',
  'nextForecast',
];

export function releasesTourStorageKey(orgId: string, productId: string): string {
  return `${ONBOARDING_STORAGE_PREFIX}.releasesTour.${orgId}.${productId}.v1`;
}

function isValidReleasesTourStep(value: string): value is ReleasesTourStep {
  return (
    value === 'welcome' ||
    value === 'calendar' ||
    value === 'mapping' ||
    value === 'plans' ||
    value === 'nextForecast' ||
    value === 'completed' ||
    value === 'skipped'
  );
}

export function getReleasesTourStep(
  orgId: string,
  productId: string
): ReleasesTourStep | undefined {
  try {
    const raw = localStorage.getItem(releasesTourStorageKey(orgId, productId));
    if (!raw) return undefined;
    return isValidReleasesTourStep(raw) ? raw : undefined;
  } catch {
    return undefined;
  }
}

export function setReleasesTourStep(
  orgId: string,
  productId: string,
  step: ReleasesTourStep
): void {
  try {
    localStorage.setItem(releasesTourStorageKey(orgId, productId), step);
  } catch {
    // ignore
  }
}

export function advanceReleasesTourStep(orgId: string, productId: string): void {
  const current = getReleasesTourStep(orgId, productId);
  if (!current || current === 'completed' || current === 'skipped') return;
  const idx = RELEASES_TOUR_ORDER.indexOf(current);
  if (idx === -1) return;
  if (idx >= RELEASES_TOUR_ORDER.length - 1) {
    setReleasesTourStep(orgId, productId, 'completed');
  } else {
    setReleasesTourStep(orgId, productId, RELEASES_TOUR_ORDER[idx + 1]);
  }
}

export function retreatReleasesTourStep(orgId: string, productId: string): void {
  const current = getReleasesTourStep(orgId, productId);
  if (!current || current === 'completed' || current === 'skipped' || current === 'welcome') return;
  const idx = RELEASES_TOUR_ORDER.indexOf(current);
  if (idx <= 0) return;
  setReleasesTourStep(orgId, productId, RELEASES_TOUR_ORDER[idx - 1]);
}

/** Dismissed prominent Calendar setup card (per org). Cleared by resetAllOnboarding. */
export function calendarSetupProminentDismissedKey(orgId: string): string {
  return `${ONBOARDING_STORAGE_PREFIX}.calendarSetupProminentDismissed.${orgId}.v1`;
}

export function isCalendarSetupProminentDismissed(orgId: string): boolean {
  try {
    return localStorage.getItem(calendarSetupProminentDismissedKey(orgId)) === '1';
  } catch {
    return false;
  }
}

export function markCalendarSetupProminentDismissed(orgId: string): void {
  try {
    localStorage.setItem(calendarSetupProminentDismissedKey(orgId), '1');
  } catch {
    // ignore
  }
}

/** Per-organization first-run wizard completion (browser). Server also stores organizations.onboarding_completed. */
export function onboardingOrgCompletedStorageKey(orgId: string): string {
  return `${ONBOARDING_STORAGE_PREFIX}.orgCompleted.${orgId}`;
}

export function markOnboardingCompleted(orgId: string): void {
  try {
    localStorage.setItem(onboardingOrgCompletedStorageKey(orgId), '1');
  } catch {
    // ignore
  }
}

function isOrgMarkedCompleteLocal(orgId: string): boolean {
  try {
    return localStorage.getItem(onboardingOrgCompletedStorageKey(orgId)) === '1';
  } catch {
    return false;
  }
}

/**
 * True when the setup wizard is done for this org (server flag or localStorage).
 * Used by routing so users are not dropped on Analytics before finishing onboarding.
 */
export function isOrganizationOnboardingComplete(
  org: { id: string; onboarding_completed?: boolean } | null | undefined
): boolean {
  if (!org?.id) return false;
  if (org.onboarding_completed === true) return true;
  return isOrgMarkedCompleteLocal(org.id);
}

export function pickOrgForOnboardingCheck(
  organizations: { id: string; onboarding_completed?: boolean; role?: string }[],
  savedOrgId: string | null
): { id: string; onboarding_completed?: boolean; role?: string } | null {
  if (!organizations.length) return null;
  const match = savedOrgId ? organizations.find((o) => o.id === savedOrgId) : null;
  const pool = match
    ? [match, ...organizations.filter((o) => o.id !== match.id)]
    : [...organizations];
  const completeFirst = pool.find((o) => o.onboarding_completed === true);
  if (completeFirst) return completeFirst;
  return match || organizations[0];
}

/**
 * Support staff viewing a customer org — bypass normal onboarding redirects that use only `organizations[]`.
 */
export function isSupportCustomerSession(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(LS_SUPPORT_MODE) === 'true' && !!localStorage.getItem(LS_ORG_ID);
  } catch {
    return false;
  }
}

/**
 * Only the org **owner** completes first-run setup. Administrators and members may use the app
 * while onboarding is unfinished (matches `OnboardingPage` redirect behavior).
 */
export function skipsOrgOnboardingWhenIncomplete(
  org: { id: string; onboarding_completed?: boolean; role?: string } | null | undefined
): boolean {
  if (!org?.id) return false;
  if (isOrganizationOnboardingComplete(org)) return false;
  if (!org.role || org.role === 'owner') return false;
  return true;
}

/** Prefix for FeatureCoach dismissals. */
export const FEATURE_COACH_STORAGE_PREFIX = `${P}.coach`;

export function featureCoachCompletedStorageKey(featureKey: string, productId?: string): string {
  if (productId) return `${FEATURE_COACH_STORAGE_PREFIX}.${featureKey}.${productId}.v2`;
  return `${FEATURE_COACH_STORAGE_PREFIX}.${featureKey}.completed`;
}

export function featureCoachSuppressAllStorageKey(featureKey: string): string {
  return `${FEATURE_COACH_STORAGE_PREFIX}.${featureKey}.suppressAll.v2`;
}

export function isFeatureCoachSuppressAll(featureKey: string): boolean {
  try {
    return localStorage.getItem(featureCoachSuppressAllStorageKey(featureKey)) === '1';
  } catch {
    return false;
  }
}

export function markFeatureCoachSuppressAll(featureKey: string): void {
  try {
    localStorage.setItem(featureCoachSuppressAllStorageKey(featureKey), '1');
  } catch {
    // ignore
  }
}

/**
 * Coach dismissed for this product (v2), or legacy global `.completed` without product scope.
 * When `productId` is set, suppress-all also hides the coach for every product.
 */
export function isFeatureCoachCompletedForProduct(featureKey: string, productId?: string): boolean {
  try {
    if (productId) {
      if (isFeatureCoachSuppressAll(featureKey)) return true;
      if (localStorage.getItem(featureCoachCompletedStorageKey(featureKey, productId)) === '1') {
        return true;
      }
      if (localStorage.getItem(featureCoachCompletedStorageKey(featureKey)) === '1') {
        return true;
      }
      return false;
    }
    return localStorage.getItem(featureCoachCompletedStorageKey(featureKey)) === '1';
  } catch {
    return false;
  }
}

export function markFeatureCoachCompletedForProduct(featureKey: string, productId?: string): void {
  try {
    if (productId) {
      localStorage.setItem(featureCoachCompletedStorageKey(featureKey, productId), '1');
      return;
    }
    localStorage.setItem(featureCoachCompletedStorageKey(featureKey), '1');
  } catch {
    // ignore
  }
}

/**
 * Clears v2 onboarding keys for one org + product (non-destructive to v1 legacy keys).
 */
export function resetProductOnboarding(orgId: string, productId: string): void {
  const suffix = `.${productId}.v2`;
  const forecastTourSuffix = `.${productId}.v1`;
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      const isOnboardingProduct = k.startsWith(ONBOARDING_STORAGE_PREFIX) && k.endsWith(suffix);
      const isCoachProduct = k.startsWith(FEATURE_COACH_STORAGE_PREFIX) && k.endsWith(suffix);
      const isForecastTourProduct =
        k.startsWith(`${ONBOARDING_STORAGE_PREFIX}.forecastTour.`) &&
        k.endsWith(forecastTourSuffix);
      const isReleasesTourProduct =
        k.startsWith(`${ONBOARDING_STORAGE_PREFIX}.releasesTour.`) &&
        k.endsWith(forecastTourSuffix);
      if (isOnboardingProduct || isCoachProduct || isForecastTourProduct || isReleasesTourProduct) {
        toRemove.push(k);
      }
    }
    toRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // ignore
  }
}

/**
 * Atlas (unified guidance layer) storage. Keys are scoped per mission + org + product.
 * Values are step ids (e.g. `'welcome'`, `'timeframe'`) or terminal markers (`'completed'`, `'skipped'`).
 * Snooze is a unix timestamp (ms) until which the Dock should hide this mission.
 */
export const ATLAS_STORAGE_PREFIX = `${P}.atlas`;

/**
 * User closed the entire Atlas dock ("Close" in panel). Per org + product;
 * if there is no product yet, `productId` is null and a sentinel segment is used.
 * Cleared by Restore Atlas in Profile. Removed by `resetAllOnboarding` (ATLAS prefix).
 */
export function atlasGuidanceDismissedKey(orgId: string, productId: string | null): string {
  const p = productId ?? '__noproduct__';
  return `${ATLAS_STORAGE_PREFIX}.guidanceDismissed.${orgId}.${p}.v1`;
}

export function isAtlasGuidanceDismissedForProduct(
  orgId: string,
  productId: string | null
): boolean {
  try {
    return localStorage.getItem(atlasGuidanceDismissedKey(orgId, productId)) === '1';
  } catch {
    return false;
  }
}

export function dismissAtlasGuidanceForProduct(orgId: string, productId: string | null): void {
  try {
    localStorage.setItem(atlasGuidanceDismissedKey(orgId, productId), '1');
  } catch {
    // ignore
  }
}

export function clearAtlasGuidanceDismissedForProduct(
  orgId: string,
  productId: string | null
): void {
  try {
    localStorage.removeItem(atlasGuidanceDismissedKey(orgId, productId));
  } catch {
    // ignore
  }
}

export function atlasMissionStepKey(missionId: string, orgId: string, productId: string): string {
  return `${ATLAS_STORAGE_PREFIX}.mission.${missionId}.${orgId}.${productId}.v1`;
}

export function getAtlasMissionStep(
  missionId: string,
  orgId: string,
  productId: string
): string | undefined {
  try {
    const raw = localStorage.getItem(atlasMissionStepKey(missionId, orgId, productId));
    return raw ?? undefined;
  } catch {
    return undefined;
  }
}

export function setAtlasMissionStep(
  missionId: string,
  orgId: string,
  productId: string,
  stepOrTerminal: string
): void {
  try {
    localStorage.setItem(atlasMissionStepKey(missionId, orgId, productId), stepOrTerminal);
  } catch {
    // ignore
  }
}

export function clearAtlasMissionStep(missionId: string, orgId: string, productId: string): void {
  try {
    localStorage.removeItem(atlasMissionStepKey(missionId, orgId, productId));
  } catch {
    // ignore
  }
}

export function atlasSnoozeKey(missionId: string, orgId: string, productId: string): string {
  return `${ATLAS_STORAGE_PREFIX}.snooze.${missionId}.${orgId}.${productId}.v1`;
}

export function getAtlasSnoozeUntil(
  missionId: string,
  orgId: string,
  productId: string
): number | null {
  try {
    const raw = localStorage.getItem(atlasSnoozeKey(missionId, orgId, productId));
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

export function setAtlasSnoozeUntil(
  missionId: string,
  orgId: string,
  productId: string,
  untilMs: number
): void {
  try {
    localStorage.setItem(atlasSnoozeKey(missionId, orgId, productId), String(untilMs));
  } catch {
    // ignore
  }
}

export function clearAtlasSnooze(missionId: string, orgId: string, productId: string): void {
  try {
    localStorage.removeItem(atlasSnoozeKey(missionId, orgId, productId));
  } catch {
    // ignore
  }
}

/** Tracks whether the user has exported at least one report (per org + product). */
export function atlasFirstReportKey(orgId: string, productId: string): string {
  return `${ATLAS_STORAGE_PREFIX}.checkpoint.firstReport.${orgId}.${productId}.v1`;
}

export function hasFirstReportExported(orgId: string, productId: string): boolean {
  try {
    return localStorage.getItem(atlasFirstReportKey(orgId, productId)) === '1';
  } catch {
    return false;
  }
}

export function markFirstReportExported(orgId: string, productId: string): void {
  try {
    localStorage.setItem(atlasFirstReportKey(orgId, productId), '1');
  } catch {
    // ignore
  }
}

/** Tracks whether the user has recorded a first commitment baseline (per org + product). */
export function atlasFirstBaselineKey(orgId: string, productId: string): string {
  return `${ATLAS_STORAGE_PREFIX}.checkpoint.firstBaseline.${orgId}.${productId}.v1`;
}

export function hasFirstBaselineRecorded(orgId: string, productId: string): boolean {
  try {
    return localStorage.getItem(atlasFirstBaselineKey(orgId, productId)) === '1';
  } catch {
    return false;
  }
}

export function markFirstBaselineRecorded(orgId: string, productId: string): void {
  try {
    localStorage.setItem(atlasFirstBaselineKey(orgId, productId), '1');
  } catch {
    // ignore
  }
}

/**
 * Calendar onboarding checkpoints (per org + product). These drive the derived
 * `completed` state of the `calendar-connect` mission — see
 * `onboardingStateAdapter.readMissionState`. Each one is set once and never
 * cleared on its own (resetAllOnboarding wipes them with the rest).
 */
function atlasCalendarSyncedKey(orgId: string, productId: string): string {
  return `${ATLAS_STORAGE_PREFIX}.checkpoint.calendarSynced.${orgId}.${productId}.v1`;
}

export function hasCalendarSyncedForProduct(orgId: string, productId: string): boolean {
  try {
    return localStorage.getItem(atlasCalendarSyncedKey(orgId, productId)) === '1';
  } catch {
    return false;
  }
}

export function markCalendarSyncedForProduct(orgId: string, productId: string): void {
  try {
    localStorage.setItem(atlasCalendarSyncedKey(orgId, productId), '1');
  } catch {
    // ignore
  }
}

function atlasAbsenceKeywordsOpenedKey(orgId: string, productId: string): string {
  return `${ATLAS_STORAGE_PREFIX}.checkpoint.absenceKeywordsOpened.${orgId}.${productId}.v1`;
}

export function hasAbsenceKeywordsOpenedForProduct(orgId: string, productId: string): boolean {
  try {
    return localStorage.getItem(atlasAbsenceKeywordsOpenedKey(orgId, productId)) === '1';
  } catch {
    return false;
  }
}

export function markAbsenceKeywordsOpenedForProduct(orgId: string, productId: string): void {
  try {
    localStorage.setItem(atlasAbsenceKeywordsOpenedKey(orgId, productId), '1');
  } catch {
    // ignore
  }
}

function atlasPublicHolidayKeywordsOpenedKey(orgId: string, productId: string): string {
  return `${ATLAS_STORAGE_PREFIX}.checkpoint.publicHolidayKeywordsOpened.${orgId}.${productId}.v1`;
}

export function hasPublicHolidayKeywordsOpenedForProduct(
  orgId: string,
  productId: string
): boolean {
  try {
    return localStorage.getItem(atlasPublicHolidayKeywordsOpenedKey(orgId, productId)) === '1';
  } catch {
    return false;
  }
}

export function markPublicHolidayKeywordsOpenedForProduct(orgId: string, productId: string): void {
  try {
    localStorage.setItem(atlasPublicHolidayKeywordsOpenedKey(orgId, productId), '1');
  } catch {
    // ignore
  }
}

function atlasVisitedReleasesKey(orgId: string, productId: string): string {
  return `${ATLAS_STORAGE_PREFIX}.checkpoint.visitedReleases.${orgId}.${productId}.v1`;
}

export function hasVisitedReleasesForProduct(orgId: string, productId: string): boolean {
  try {
    return localStorage.getItem(atlasVisitedReleasesKey(orgId, productId)) === '1';
  } catch {
    return false;
  }
}

export function markVisitedReleasesForProduct(orgId: string, productId: string): void {
  try {
    localStorage.setItem(atlasVisitedReleasesKey(orgId, productId), '1');
  } catch {
    // ignore
  }
}

/**
 * Clears all onboarding flags (welcome tour, hints, toasts, page coaches).
 * Coach keys and Atlas keys are also cleared so "Reset onboarding" wipes the full layer.
 * Also clears legacy svodion.coach.* and svodion.atlas.* keys from previous brand prefixes.
 */
export function resetAllOnboarding(): void {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (
        k.startsWith(ONBOARDING_STORAGE_PREFIX) ||
        k.startsWith(`${P}.coach.`) ||
        k.startsWith(ATLAS_STORAGE_PREFIX) ||
        k.startsWith('svodion.coach.') ||
        k.startsWith('svodion.atlas.')
      ) {
        toRemove.push(k);
      }
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}
